import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Popconfirm, Select, Switch, Tag } from "antd";
import { ApiOutlined, CheckCircleOutlined, WarningOutlined } from "@ant-design/icons";
import {
  disconnectProspectHubspot,
  getProspectHubspot,
  listProspectHubspotPipelines,
  saveProspectHubspotSettings,
  startProspectHubspotOAuth,
} from "./prospectService";
import styles from "./style.module.scss";

const STATUS_COLORS = { done: "green", pending: "blue", failed: "red", skipped: "default" };

/** HubSpot for the whole account: Qualified Prospects become contact + note + deal, handoffs a task. */
const HubSpotConnectCard = () => {
  const { t } = useTranslation();
  const [state, setState] = useState(null);
  const [settings, setSettings] = useState(null);
  const [pipelines, setPipelines] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [errorKey, setErrorKey] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await getProspectHubspot();
      setState(res);
      setSettings(res.settings || null);
      if (res.connected) {
        listProspectHubspotPipelines()
          .then((out) => setPipelines(out.pipelines || []))
          .catch(() => setPipelines([]));
      }
    } catch (_) {
      setState({ connected: false, configured: false, failed: true });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const connect = async () => {
    setBusy(true);
    setErrorKey("");
    try {
      const started = await startProspectHubspotOAuth();
      if (started?.authorize_url) window.location.assign(started.authorize_url);
      else setErrorKey("sonic_prospect.hubspot_unconfigured");
    } catch (_) {
      setErrorKey("sonic_prospect.hubspot_error");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    setErrorKey("");
    try {
      await disconnectProspectHubspot();
      setPipelines([]);
      await load();
    } catch (_) {
      setErrorKey("sonic_prospect.hubspot_error");
    } finally {
      setBusy(false);
    }
  };

  const update = (patch) => {
    setSaved(false);
    setSettings((prev) => ({ ...prev, ...patch }));
  };

  const save = async () => {
    setBusy(true);
    setErrorKey("");
    try {
      const res = await saveProspectHubspotSettings(settings);
      setSettings(res.settings);
      setSaved(true);
    } catch (_) {
      setErrorKey("sonic_prospect.hubspot_error");
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;
  const connected = !!state.connected;
  const needsReconnect = connected && state.status === "reconnect_required";
  const pipeline = pipelines.find((p) => p.id === settings?.pipeline_id);

  return (
    <div className={styles.igCard}>
      <div className={styles.igHeader}>
        <ApiOutlined />
        <p className={styles.igTitle}>{t("sonic_prospect.hubspot_title")}</p>
        {needsReconnect ? (
          <Tag icon={<WarningOutlined />} color="orange">
            {t("sonic_prospect.hubspot_reconnect_tag")}
          </Tag>
        ) : connected ? (
          <Tag icon={<CheckCircleOutlined />} color="green">
            {t("sonic_prospect.hubspot_connected")}
          </Tag>
        ) : (
          <Tag>{t("sonic_prospect.hubspot_not_connected")}</Tag>
        )}
      </div>
      <p className={styles.igIntro}>{t("sonic_prospect.hubspot_intro")}</p>

      {!connected || needsReconnect ? (
        <div className={styles.igActions}>
          {needsReconnect ? <p className={styles.igError}>{t("sonic_prospect.hubspot_reconnect_body")}</p> : null}
          <Button type="primary" icon={<ApiOutlined />} loading={busy} onClick={connect} disabled={state.configured === false}>
            {t(needsReconnect ? "sonic_prospect.hubspot_reconnect" : "sonic_prospect.hubspot_connect")}
          </Button>
          {state.configured === false ? <p className={styles.igMeta}>{t("sonic_prospect.hubspot_unconfigured")}</p> : null}
        </div>
      ) : null}

      {connected && settings ? (
        <>
          {state.hub_domain || state.portal_id ? (
            <p className={styles.igMeta}>
              {t("sonic_prospect.hubspot_account")}: {state.hub_domain || state.portal_id}
            </p>
          ) : null}
          <div className={styles.hubspotRow}>
            <Switch checked={settings.sync_qualified} onChange={(v) => update({ sync_qualified: v })} />
            <span>{t("sonic_prospect.hubspot_sync_qualified")}</span>
          </div>
          <div className={styles.hubspotRow}>
            <Switch
              checked={settings.create_deal}
              disabled={!settings.sync_qualified}
              onChange={(v) => update({ create_deal: v })}
            />
            <span>{t("sonic_prospect.hubspot_create_deal")}</span>
          </div>
          {settings.sync_qualified && settings.create_deal ? (
            <div className={styles.hubspotSelects}>
              <Select
                value={settings.pipeline_id || undefined}
                placeholder={t("sonic_prospect.hubspot_pipeline_default")}
                allowClear
                onChange={(id) => {
                  const next = pipelines.find((p) => p.id === id);
                  update({ pipeline_id: id || "", stage_id: next?.stages?.[0]?.id || "" });
                }}
                options={pipelines.map((p) => ({ value: p.id, label: p.label }))}
              />
              {pipeline ? (
                <Select
                  value={settings.stage_id || undefined}
                  onChange={(id) => update({ stage_id: id })}
                  options={(pipeline.stages || []).map((s) => ({ value: s.id, label: s.label }))}
                />
              ) : null}
            </div>
          ) : null}
          <div className={styles.hubspotRow}>
            <Switch checked={settings.sync_handoff} onChange={(v) => update({ sync_handoff: v })} />
            <span>{t("sonic_prospect.hubspot_sync_handoff")}</span>
          </div>
          <div className={styles.igActions}>
            <Button type="primary" loading={busy} onClick={save}>
              {t("sonic_prospect.btn_save")}
            </Button>{" "}
            <Popconfirm
              title={t("sonic_prospect.hubspot_disconnect_confirm")}
              okText={t("sonic_prospect.hubspot_disconnect")}
              cancelText={t("sonic_prospect.confirm_cancel")}
              onConfirm={disconnect}
            >
              <Button danger type="text" loading={busy}>
                {t("sonic_prospect.hubspot_disconnect")}
              </Button>
            </Popconfirm>
            {saved ? <span className={styles.igMeta}> {t("sonic_prospect.hubspot_saved")}</span> : null}
          </div>
          {(state.recent || []).length ? (
            <div className={styles.hubspotRecent}>
              <p className={styles.igMeta}>{t("sonic_prospect.hubspot_recent")}</p>
              {state.recent.slice(0, 5).map((job) => (
                <div key={`${job.kind}-${job.session_id}`} className={styles.hubspotJob}>
                  <Tag color={STATUS_COLORS[job.status] || "default"}>{t(`sonic_prospect.hubspot_status_${job.status}`)}</Tag>
                  <span>{t(`sonic_prospect.hubspot_kind_${job.kind}`)}</span>
                  <span className={styles.igMeta}>{job.created_at ? new Date(job.created_at).toLocaleString() : ""}</span>
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
      {errorKey ? <p className={styles.igError}>{t(errorKey)}</p> : null}
    </div>
  );
};

export default HubSpotConnectCard;
